import { token } from '@/infrastructure/container';

// The only place `console.*` is allowed. Everything else takes ILogger.

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

export interface ILogger {
  debug(msg: string, ...args: unknown[]): void;
  info(msg: string, ...args: unknown[]): void;
  warn(msg: string, ...args: unknown[]): void;
  error(msg: string, ...args: unknown[]): void;
  setLevel(level: LogLevel): void;
}

const RANK: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40, silent: 50 };

// Production is `error`, dev is `info`, so a debug line costs nothing in a shipped build and does
// not need stripping before commit. No loglevel dependency: the whole knob is one comparison.
export class ConsoleLogger implements ILogger {
  private level: LogLevel;

  constructor(level: LogLevel = import.meta.env.PROD ? 'error' : 'info') {
    this.level = level;
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  debug(msg: string, ...args: unknown[]): void {
    if (this.enabled('debug')) console.debug(`[grading-day] ${msg}`, ...args);
  }
  info(msg: string, ...args: unknown[]): void {
    if (this.enabled('info')) console.info(`[grading-day] ${msg}`, ...args);
  }
  warn(msg: string, ...args: unknown[]): void {
    if (this.enabled('warn')) console.warn(`[grading-day] ${msg}`, ...args);
  }
  error(msg: string, ...args: unknown[]): void {
    if (this.enabled('error')) console.error(`[grading-day] ${msg}`, ...args);
  }

  private enabled(level: LogLevel): boolean {
    return RANK[level] >= RANK[this.level];
  }
}

// Swallows everything. Hand this to a store under test so a red run's output is the assertion
// failure, not a wall of log lines.
export class SilentLogger implements ILogger {
  debug(): void {}
  info(): void {}
  warn(): void {}
  error(): void {}
  setLevel(): void {}
}

// Records every line and prints it. On a red run Vitest dumps the captured console for the
// failing test, so wiring this into a trace sink turns the run into a timeline: which event
// fired, which store reacted, in order. Green runs stay quiet because Vitest only prints the
// capture for failures.
export class CaptureLogger implements ILogger {
  readonly lines: string[] = [];

  debug(msg: string, ...args: unknown[]): void {
    this.emit('DEBUG', [msg, ...args]);
  }
  info(msg: string, ...args: unknown[]): void {
    this.emit('INFO', [msg, ...args]);
  }
  warn(msg: string, ...args: unknown[]): void {
    this.emit('WARN', [msg, ...args]);
  }
  error(msg: string, ...args: unknown[]): void {
    this.emit('ERROR', [msg, ...args]);
  }
  setLevel(): void {}

  private emit(level: string, args: unknown[]): void {
    const line = `${level} ${args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ')}`;
    this.lines.push(line);
    console.log(line);
  }
}

export const TOKEN_ILogger = token<ILogger>('ILogger');
